import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '../lib/supabase';

export function useMyProducts() {
  const queryClient = useQueryClient();

  // Set up real-time subscription for product quantity changes
  useEffect(() => {
    const channel = supabase
      .channel('product-quantity-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'products',
          filter: 'status=eq.active'
        },
        (payload) => {
          console.log('Product updated:', payload);
          // Invalidate queries when product quantity changes
          queryClient.invalidateQueries({ queryKey: ['my-products'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const query = useQuery({
    queryKey: ['my-products'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return [];

      console.log('Fetching products for user:', session.user.id);

      // Get active vendors that are not deleted
      const { data: vendors, error: vendorsError } = await supabase
        .from('vendors')
        .select('id, store_name')
        .eq('user_id', session.user.id)
        .eq('status', 'active')
        .not('is_deleted', 'eq', true);

      if (vendorsError) {
        console.error('Error fetching vendors:', vendorsError);
        throw vendorsError;
      }

      if (!vendors || vendors.length === 0) {
        console.log('No vendors found for user');
        return [];
      }

      console.log(`Found ${vendors.length} vendors for user`);
      
      // If multiple vendors exist, get the most recently created one
      // This assumes the most recent vendor is the one being currently used
      // For a more robust solution, we'd need to track the "primary" vendor
      const vendorIds = vendors.map(vendor => vendor.id);
      
      console.log('Getting products for vendor IDs:', vendorIds);

      // Fetch all products associated with any of the user's vendor accounts
      // Filter out products with quantity 0 or less - they should not appear in Sell tab
      const { data: allProducts, error: productsError } = await supabase
        .from('products')
        .select('*')
        .in('vendor_id', vendorIds)
        .eq('status', 'active')
        .gt('quantity', 0)  // Only show products with quantity > 0
        .order('created_at', { ascending: false });

      if (productsError) {
        console.error('Error fetching products:', productsError);
        throw productsError;
      }
      
      console.log('Found products count:', allProducts?.length || 0);
      return allProducts || [];
    },
    // Add these options to ensure fresh data
    staleTime: 1000, // 1 second
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const deleteProduct = useMutation({
    mutationFn: async (productId: string) => {
      const { error } = await supabase
        .from('products')
        .update({ status: 'deleted' })
        .eq('id', productId);

      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate all product-related queries to refresh all screens
      queryClient.invalidateQueries({ queryKey: ['my-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });

  return {
    ...query,
    deleteProduct: deleteProduct.mutate,
  };
} 